import React from "react";
import { Box } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PaletteColor } from "@mui/material/styles";
import { Theme } from "@mui/material/styles";
import MarkChatUnreadIcon from "@mui/icons-material/MarkChatUnread";
import MarkChatReadIcon from "@mui/icons-material/MarkChatRead";
import DeleteIcon from "@mui/icons-material/Delete";
import { AuthorizedEntityComponent, convertDateTime, CoreFrontendNotificationDto, isValueDefined } from "@mm-products/java-multima-framework";
import TaxGuruIconButton from "../../components/button/TaxGuruIconButton";
import NotificationDownloadButton from "./NotificationDownloadButton";
import { useTranslation } from "react-i18next";
import TaxGuruTooltip from "../../components/filter/TaxGuruTooltip";
import Stack from "@mui/material/Stack";
import notificationState = CoreFrontendNotificationDto.notificationState;

const parseMuiPalette = (notification: CoreFrontendNotificationDto, theme: Theme): PaletteColor => {
    switch (notification.notificationType?.toLowerCase()) {
        case "success":
            return theme.palette.success;
        case "info":
            return theme.palette.info;
        case "warning":
            return theme.palette.warning;
        default:
            return theme.palette.error;
    }
};

export type NotificationCenterRowProps = {
    notification: CoreFrontendNotificationDto;
    onRemoveItem: (notification: CoreFrontendNotificationDto) => void;
    onMarkRead: (notification: CoreFrontendNotificationDto, read: boolean) => void;
};

const NotificationCenterRow = ({ notification, onRemoveItem, onMarkRead }: NotificationCenterRowProps) => {
    const theme = useTheme();
    const muiPalette = parseMuiPalette(notification, theme);
    const { t } = useTranslation();

    const typeLowerCase = notification.notificationType?.toLowerCase();

    return (
        <Box sx={{ borderColor: "divider", borderStyle: "solid" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-start", paddingBottom: 1 }}>
                <Typography
                    sx={{
                        paddingX: 1,
                        fontSize: "0.8rem",
                        borderColor: "divider",
                        borderStyle: "solid",
                        borderTopWidth: 0,
                        borderLeftWidth: 0,
                    }}
                >
                    {convertDateTime(notification.created)}
                </Typography>
            </Box>
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <>
                    {typeLowerCase === "success" && <CheckCircleIcon color="success" />}
                    {typeLowerCase === "info" && <InfoIcon color="info" />}
                    {typeLowerCase === "warning" && <WarningIcon color="warning" />}
                    {typeLowerCase === "error" && <ErrorIcon color="error" />}
                </>
                <Typography variant="body2" sx={{ color: muiPalette.main, flexGrow: 1 }}>
                    {notification.message}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    {isValueDefined(notification.downloadUrl) && <NotificationDownloadButton notification={notification} />}
                    {notification.notificationState === notificationState.READ && (
                        <AuthorizedEntityComponent requiredPermissions={["update"]} unauthorizedMode="no-render" entityPermissions={notification}>
                            <TaxGuruTooltip title={t("actions.markAsUnread")}>
                                <TaxGuruIconButton size="small" onClick={() => onMarkRead(notification, false)}>
                                    <MarkChatReadIcon />
                                </TaxGuruIconButton>
                            </TaxGuruTooltip>
                        </AuthorizedEntityComponent>
                    )}
                    {notification.notificationState === notificationState.UNREAD && (
                        <AuthorizedEntityComponent requiredPermissions={["update"]} unauthorizedMode="no-render" entityPermissions={notification}>
                            <TaxGuruTooltip title={t("actions.markAsRead")}>
                                <TaxGuruIconButton size="small" onClick={() => onMarkRead(notification, true)}>
                                    <MarkChatUnreadIcon />
                                </TaxGuruIconButton>
                            </TaxGuruTooltip>
                        </AuthorizedEntityComponent>
                    )}
                    <AuthorizedEntityComponent requiredPermissions={["delete"]} unauthorizedMode="no-render" entityPermissions={notification}>
                        <TaxGuruTooltip title={t("actions.delete")}>
                            <TaxGuruIconButton size="small" onClick={() => onRemoveItem(notification)}>
                                <DeleteIcon />
                            </TaxGuruIconButton>
                        </TaxGuruTooltip>
                    </AuthorizedEntityComponent>
                </Box>
            </Stack>
        </Box>
    );
};

export default NotificationCenterRow;
